// S9c: the phase model inside the tracker.
//
// Two jobs, both on the pulse lattice the tracker found (two pulses a beat; class of a pulse = its position in the list mod 4):
//   1. `decidePhase`: which of the 4 classes is the odd beat, from the audio (the logistic model of S9a), with the margins that say how sure it is. The beat is then
//      every second pulse of the odd class's parity, and the taps only choose WHICH odd count is 1 (`oddParity`, used by `tapResidue`).
//   2. `holdPhase`: the model read in sliding windows along the lattice. Where the rotation changes against the established one and stays changed (one position: a slide
//      to the "&"; two: a whole-beat slip; three: a slide the other way) the change is located and one pulse is removed or inserted at the weakest point, so that the
//      rotation, and the count with it, is continuous from there on. A change of four counts (8 pulses) is invisible to a 4-cycle model and is never touched: it is
//      a real change of the music, and belongs to a later step. A change that lasts one window is not acted on.
//
// Windows are decided together, not one by one: a 4-state Markov chain over the windows (emission: the model's log-probabilities of the window times `evidence`, a
// switch costs `switchNats`), Viterbi, then every run shorter than `minRun` windows is merged into its neighbour. Windows overlap by 75%, so `evidence` below 1 is
// the honest weight of a window; the cost of a switch is what keeps a single odd window from being believed.

import type { BandEnvelopes } from './bands.js';
import { classMeans, pulseValues, rotationPatterns } from './fold.js';
import { rotationLogProbs, type PhaseParams } from './model.js';
import { PHASE_PARAMS } from './params.js';

/** The model the tracker uses: the logistic one the bench chose on `train` (`params.ts`). */
export const PHASE_MODEL: PhaseParams = { kind: 'logistic', mean: PHASE_PARAMS.logistic.mean, scale: PHASE_PARAMS.logistic.scale, weights: PHASE_PARAMS.logistic.weights };
export const PHASE_HALF_WINDOW_S = PHASE_PARAMS.halfWindowS.logistic;

export interface PhaseHoldOptions {
  /** Window length and hop, seconds. */
  readonly windowS: number;
  readonly hopS: number;
  /** Cost, in nats, of a change of rotation between two windows. */
  readonly switchNats: number;
  /** Weight of a window's log-probabilities (windows overlap, so their evidence is not independent). */
  readonly evidence: number;
  /** A run of fewer windows than this is not a change (merged into its neighbour). */
  readonly minRun: number;
  /** Windows start no earlier than this (the model was fitted on lattices from 5 s: intros and counts-in are not music it learned). */
  readonly fromS: number;
  /** Shortest lattice (seconds) on which a change is acted on. Below it the lattice is only read, not repaired. */
  readonly minSpanS: number;
}

export const PHASE_HOLD: PhaseHoldOptions = { windowS: 20, hopS: 5, switchNats: 4, evidence: 1, minRun: 2, fromS: 5, minSpanS: 40 };

/** One repair: a pulse removed (`delta` +1 per pulse) or inserted (-1), at `t` seconds (the change's location). */
export interface PhaseRepair {
  readonly t: number;
  /** Rotation change repaired, in pulses (1, 2 or 3). */
  readonly pulses: number;
  /** Pulses removed (positive) or inserted (negative) in all. */
  readonly net: number;
}

export interface PhaseDecision {
  /** Pulse parity (0: the list's even pulses, 1: the odd) that the audio says are the beats, and how sure: |ln P(parity 0) - ln P(parity 1)| in nats. */
  readonly parity: 0 | 1;
  readonly margin: number;
  /** Class (0 to 3) of the odd beat, given the parity; and how sure: the log-probability gap between that class and the one two pulses on. */
  readonly oddClass: number;
  readonly oddMargin: number;
  readonly lp: readonly number[];
}

// ---------------------------------------------------------------- folding a stretch of the lattice

function lowerBound(a: readonly number[], t: number): number {
  let lo = 0, hi = a.length;
  while (lo < hi) { const m = (lo + hi) >> 1; if (a[m]! < t) lo = m + 1; else hi = m; }
  return lo;
}

/** The model's log-probabilities of the 4 rotations from pulses i0..i1-1 of a lattice (class = list index mod 4). Null when a class has too few pulses. */
function lpRange(model: PhaseParams, vals: readonly Float64Array[], i0: number, i1: number): number[] | null {
  if (i1 - i0 < 12) return null;
  const idx = Array.from({ length: i1 - i0 }, (_, k) => i0 + k);
  const pats = rotationPatterns(classMeans(vals.slice(i0, i1), idx, 4));
  return pats ? rotationLogProbs(model, pats) : null;
}

const logSumExp2 = (a: number, b: number) => { const m = Math.max(a, b); return m + Math.log(Math.exp(a - m) + Math.exp(b - m)); };

/** The decision from the 4 log-probabilities. */
export function decisionOf(lp: readonly number[]): PhaseDecision {
  const l0 = logSumExp2(lp[0]!, lp[2]!), l1 = logSumExp2(lp[1]!, lp[3]!);
  const parity: 0 | 1 = l1 > l0 ? 1 : 0;
  const o = oddWithin(lp, parity);
  return { parity, margin: Math.abs(l0 - l1), oddClass: o.cls, oddMargin: o.margin, lp };
}

/** Which class of parity `p` is the odd beat (class p or p + 2), and by how many nats. */
export function oddWithin(lp: readonly number[], p: number): { cls: number; margin: number } {
  const a = lp[p]!, b = lp[p + 2]!;
  return { cls: a >= b ? p : p + 2, margin: Math.abs(a - b) };
}

// ---------------------------------------------------------------- the decision

/**
 * The audio's decision on a lattice: the model read over `spanS` seconds either side of `centreT` (clamped to the lattice and to `fromS`), or null when the lattice is too short.
 * `times` are pulse times, `vals` their band values (`pulseValues`).
 */
export function decidePhase(model: PhaseParams, times: readonly number[], vals: readonly Float64Array[], centreT: number, spanS = 20, fromS = PHASE_HOLD.fromS): PhaseDecision | null {
  let i0 = lowerBound(times, Math.max(fromS, centreT - spanS)), i1 = lowerBound(times, centreT + spanS);
  if (i1 - i0 < 24) { i0 = 0; i1 = times.length; } // a short lattice, or taps at its edge: read all of it
  const lp = lpRange(model, vals, i0, i1);
  return lp ? decisionOf(lp) : null;
}

// ---------------------------------------------------------------- windows and the rotation path

interface Win { readonly from: number; readonly to: number; readonly i0: number; readonly i1: number; readonly lp: number[] | null }

function windows(model: PhaseParams, times: readonly number[], vals: readonly Float64Array[], o: PhaseHoldOptions): Win[] {
  const start = Math.max(times[0]!, o.fromS), end = times.at(-1)!;
  const froms: number[] = [];
  for (let s = start; s + o.windowS <= end + 1e-9; s += o.hopS) froms.push(s);
  if (froms.length && end - o.windowS > froms.at(-1)! + 1) froms.push(end - o.windowS); // the last window ends at the end
  if (!froms.length && end - start >= o.windowS / 2) froms.push(start);
  return froms.map((from) => {
    const to = Math.min(end + 1e-9, from + o.windowS), i0 = lowerBound(times, from), i1 = lowerBound(times, to);
    return { from, to, i0, i1, lp: lpRange(model, vals, i0, i1) };
  });
}

/** Viterbi over the windows (4 states, switch cost) and then every run of fewer than `minRun` windows merged into a neighbour. State = class of the odd beat. */
export function rotationPath(ws: readonly { readonly lp: readonly number[] | null }[], o: Pick<PhaseHoldOptions, 'switchNats' | 'evidence' | 'minRun'>): number[] {
  const n = ws.length;
  if (n === 0) return [];
  const score: number[][] = [], from: number[][] = [];
  for (let w = 0; w < n; w++) {
    const emit = (s: number) => o.evidence * (ws[w]!.lp?.[s] ?? 0);
    if (w === 0) { score.push([0, 1, 2, 3].map(emit)); from.push([0, 0, 0, 0]); continue; }
    const prev = score[w - 1]!;
    let best = 0;
    for (let s = 1; s < 4; s++) if (prev[s]! > prev[best]!) best = s;
    const row: number[] = [], bk: number[] = [];
    for (let s = 0; s < 4; s++) {
      const stay = prev[s]!, move = prev[best]! - o.switchNats;
      if (best !== s && move > stay) { row.push(emit(s) + move); bk.push(best); } else { row.push(emit(s) + stay); bk.push(s); }
    }
    score.push(row); from.push(bk);
  }
  const path = new Array<number>(n);
  let s = 0;
  for (let k = 1; k < 4; k++) if (score[n - 1]![k]! > score[n - 1]![s]!) s = k;
  for (let w = n - 1; w >= 0; w--) { path[w] = s; s = from[w]![s]!; }
  // runs shorter than minRun are not changes
  for (let guard = 0; guard < n; guard++) {
    let changed = false;
    let i = 0;
    while (i < n) {
      let j = i;
      while (j + 1 < n && path[j + 1] === path[i]) j++;
      if (j - i + 1 < o.minRun && n > 1) {
        const left = i > 0 ? path[i - 1]! : null, right = j + 1 < n ? path[j + 1]! : null;
        let into = left ?? right!;
        if (left !== null && right !== null && left !== right) {
          const ev = (st: number) => { let a = 0; for (let w = i; w <= j; w++) a += ws[w]!.lp?.[st] ?? 0; return a; };
          into = ev(right) > ev(left) ? right : left;
        }
        if (into !== path[i]) { for (let w = i; w <= j; w++) path[w] = into; changed = true; }
      }
      i = j + 1;
    }
    if (!changed) break;
  }
  return path;
}

// ---------------------------------------------------------------- holding the phase

export interface HoldResult {
  /** The lattice after the repairs (the input array when there are none). */
  readonly times: number[];
  readonly repairs: PhaseRepair[];
  /** The window path (class of the odd beat per window, before repair), for the bench's diagnosis. */
  readonly path: readonly number[];
  readonly windowFrom: readonly number[];
}

/**
 * Find the changes of rotation along a pulse lattice and repair them, so that the class of the odd beat is the same from one end to the other.
 * `strength(t)` is the onset strength near time t (the tracker's envelope, the largest value within a few frames): a pulse that is removed is the weakest one near the change.
 */
export function holdPhase(model: PhaseParams, bands: BandEnvelopes, pulses: readonly number[], strength: (t: number) => number, o: PhaseHoldOptions = PHASE_HOLD): HoldResult {
  const none: HoldResult = { times: [...pulses], repairs: [], path: [], windowFrom: [] };
  if (pulses.length < 24 || pulses.at(-1)! - pulses[0]! < o.minSpanS) return none;
  const vals = pulseValues(bands, pulses, PHASE_HALF_WINDOW_S);
  const ws = windows(model, pulses, vals, o);
  const path = rotationPath(ws, o);
  const base: HoldResult = { ...none, path, windowFrom: ws.map((w) => w.from) };
  // segments: [first window, last window, state]
  const segs: { a: number; b: number; s: number }[] = [];
  path.forEach((s, w) => { if (segs.length && segs.at(-1)!.s === s) segs.at(-1)!.b = w; else segs.push({ a: w, b: w, s }); });
  if (segs.length < 2) return base;

  const span = o.windowS / 2;
  const removeAt = new Set<number>(); // original indices of pulses removed
  const insertAfter: number[] = []; // original indices after which a midpoint is inserted
  const repairs: PhaseRepair[] = [];
  let cum = 0; // net pulses removed so far (a removal moves the classes after it down by one)
  const target = segs[0]!.s;
  for (let k = 1; k < segs.length; k++) {
    const prev = segs[k - 1]!, cur = segs[k]!;
    const d = (((cur.s - cum - target) % 4) + 4) % 4;
    if (d === 0) continue;
    // where: the split of the pulses that best fits "the old rotation before it, the new one after it" (half-windows either side), searched between the two windows' centres
    const centre = (w: number) => (ws[w]!.from + ws[w]!.to) / 2;
    const lo = lowerBound(pulses, centre(prev.b) - o.hopS / 2), hi = lowerBound(pulses, centre(cur.a) + o.hopS / 2);
    let bestK = Math.min(hi, pulses.length - 2), bestScore = -Infinity;
    for (let kk = Math.max(lo, 1); kk <= Math.min(hi, pulses.length - 2); kk++) {
      const L = lpRange(model, vals, lowerBound(pulses, pulses[kk]! - span), kk), R = lpRange(model, vals, kk, lowerBound(pulses, pulses[kk]! + span));
      const sc = (L?.[prev.s] ?? 0) + (R?.[cur.s] ?? 0);
      if (sc > bestScore) { bestScore = sc; bestK = kk; }
    }
    const tSplit = pulses[bestK]!;
    // the weakest pulse within a second of the split (never the first or last pulse, never one already removed)
    const near = [] as number[];
    for (let i = Math.max(1, lowerBound(pulses, tSplit - 1)); i < Math.min(pulses.length - 1, lowerBound(pulses, tSplit + 1)); i++) if (!removeAt.has(i)) near.push(i);
    if (near.length === 0) continue;
    // A change of d pulses is d removed or 4 - d inserted (and d = 2: two removed or two inserted). Take the one that leaves the net number of pulses added or removed so far
    // nearest zero: a lattice that lost a beat and found it again has lost nothing, and two repairs in the same direction would have shifted the count by two beats. On a tie
    // (the first repair of d = 2): remove if the lattice is denser around the change than further out (it gained pulses), else insert; d = 1 removes, d = 3 inserts.
    const options = d === 1 ? [1, -3] : d === 3 ? [-1, 3] : [2, -2];
    const cost = (net: number) => Math.abs(cum + net);
    let net = options[0]!;
    if (cost(options[1]!) < cost(options[0]!)) net = options[1]!;
    else if (cost(options[1]!) === cost(options[0]!) && d === 2) {
      const dense = (a: number, b: number) => { const i0 = lowerBound(pulses, a), i1 = lowerBound(pulses, b); return i1 - i0 > 3 ? (pulses[i1 - 1]! - pulses[i0]!) / (i1 - i0 - 1) : NaN; };
      const here = dense(tSplit - 5, tSplit + 5);
      const sides = [dense(tSplit - 35, tSplit - 5), dense(tSplit + 5, tSplit + 35)].filter(Number.isFinite);
      net = sides.length && here > sides.reduce((a, b) => a + b, 0) / sides.length ? -2 : 2;
    }
    const remove = net > 0, count = Math.abs(net);
    if (remove) {
      const chosen: number[] = [];
      for (let c = 0; c < count; c++) {
        const cand = near.filter((i) => !chosen.some((j) => Math.abs(i - j) <= 1)); // not next to the first one: two gaps of 2 intervals, not one of 3
        const pool = cand.length ? cand : near.filter((i) => !chosen.includes(i));
        if (!pool.length) break;
        chosen.push(pool.reduce((b, i) => (strength(pulses[i]!) < strength(pulses[b]!) ? i : b)));
      }
      chosen.forEach((i) => removeAt.add(i));
      cum += chosen.length;
      repairs.push({ t: tSplit, pulses: d, net: chosen.length });
    } else {
      const chosen: number[] = [];
      for (let c = 0; c < count; c++) {
        const pool = near.filter((i) => !chosen.includes(i));
        if (!pool.length) break;
        chosen.push(pool.reduce((b, i) => (pulses[i + 1]! - pulses[i]! > pulses[b + 1]! - pulses[b]! ? i : b))); // the longest interval
      }
      chosen.forEach((i) => insertAfter.push(i));
      cum -= chosen.length;
      repairs.push({ t: tSplit, pulses: d, net: -chosen.length });
    }
  }
  if (!repairs.length) return base;
  const out: number[] = [];
  pulses.forEach((t, i) => {
    if (!removeAt.has(i)) out.push(t);
    if (insertAfter.includes(i) && i + 1 < pulses.length) out.push((t + pulses[i + 1]!) / 2);
  });
  return { ...base, times: out, repairs };
}
